import React, { useState, useMemo } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { Input } from "./ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "./ui/tabs";
import { 
  Code, 
  Copy, 
  Download, 
  Search,
  Filter,
  Eye,
  EyeOff,
  ChevronRight,
  ChevronDown,
  Database,
  Settings,
  Braces,
  List,
  FileText,
  Check
} from "lucide-react";
import { toast } from "sonner";

const JsonViewer = ({ 
  entities, 
  sources, 
  targets, 
  sourceFilterOptions, 
  targetConnectionFields, 
  loading 
}) => {
  const [activeTab, setActiveTab] = useState("entities");
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedFormat, setSelectedFormat] = useState("formatted");
  const [expandedItems, setExpandedItems] = useState(new Set());
  const [filterType, setFilterType] = useState("all");

  // Data mapping for different tabs
  const dataMap = {
    entities: entities,
    sources: sources,
    targets: targets,
    sourceFilters: sourceFilterOptions,
    targetFields: targetConnectionFields
  };

  const currentData = dataMap[activeTab] || [];

  // Filter data based on search and type
  const filteredData = useMemo(() => {
    let filtered = currentData;

    // Apply search filter
    if (searchTerm) {
      filtered = filtered.filter(item => {
        const searchStr = JSON.stringify(item).toLowerCase();
        return searchStr.includes(searchTerm.toLowerCase());
      });
    }

    // Apply type filter
    if (filterType !== "all") {
      switch (activeTab) {
        case "entities":
          filtered = filtered.filter(item => {
            if (filterType === "with-dependencies") return item.dependentTargets && item.dependentTargets.length > 0;
            if (filterType === "with-incompatibles") return item.incompatible && item.incompatible.length > 0;
            if (filterType === "with-delimiter") return item.isDelimiterEnabled;
            return true;
          });
          break;
        case "sources":
          filtered = filtered.filter(item => {
            if (filterType === "with-pagination") return item.paginationMethod;
            if (filterType === "with-auth") return item.sourceAuthType;
            return true;
          });
          break;
        case "targets":
          filtered = filtered.filter(item => {
            if (filterType === "main-targets") return item.isMainTarget;
            if (filterType === "mapping-targets") return item.isMappingTarget;
            if (filterType === "api-targets") return item.target_type === "API";
            return true;
          });
          break;
      }
    }

    return filtered;
  }, [currentData, searchTerm, filterType, activeTab]);

  const toggleExpanded = (id) => {
    const newExpanded = new Set(expandedItems);
    if (newExpanded.has(id)) {
      newExpanded.delete(id);
    } else {
      newExpanded.add(id);
    }
    setExpandedItems(newExpanded);
  };

  const copyToClipboard = async (data) => {
    try {
      await navigator.clipboard.writeText(JSON.stringify(data, null, 2));
      toast.success("Copied to clipboard!");
    } catch (err) {
      toast.error("Failed to copy to clipboard");
    }
  };

  const downloadJson = (data, filename) => {
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${filename}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success("File downloaded successfully!");
  };

  const formatJson = (data) => {
    if (selectedFormat === "compact") {
      return JSON.stringify(data);
    }
    return JSON.stringify(data, null, 2);
  };

  const renderJsonObject = (obj, level = 0, parentKey = "") => {
    if (obj === null || obj === undefined) {
      return <span className="text-slate-400">null</span>;
    }

    if (typeof obj !== 'object') {
      const valueClass = typeof obj === 'string' ? 'text-green-700' :
                        typeof obj === 'number' ? 'text-blue-700' :
                        typeof obj === 'boolean' ? 'text-purple-700' : 'text-slate-700';
      
      return (
        <span className={`${valueClass} font-mono`}>
          {typeof obj === 'string' ? `"${obj}"` : String(obj)}
        </span>
      );
    }

    if (Array.isArray(obj)) {
      if (obj.length === 0) return <span className="text-slate-400">[]</span>;
      
      return (
        <div className="ml-4">
          <span className="text-slate-600">[</span>
          {obj.map((item, index) => (
            <div key={index} className="ml-4 border-l-2 border-slate-100 pl-2">
              <span className="text-slate-500 text-xs">{index}:</span>{' '}
              {renderJsonObject(item, level + 1, `${parentKey}[${index}]`)}
              {index < obj.length - 1 && <span className="text-slate-600">,</span>}
            </div>
          ))}
          <span className="text-slate-600">]</span>
        </div>
      );
    }

    const keys = Object.keys(obj);
    if (keys.length === 0) return <span className="text-slate-400">{'{}'}</span>;

    return (
      <div className="ml-4">
        <span className="text-slate-600">{'{'}</span>
        {keys.map((key, index) => (
          <div key={key} className="ml-4 border-l-2 border-slate-100 pl-2">
            <span className="text-red-700 font-mono">"{key}"</span>
            <span className="text-slate-600">: </span>
            {renderJsonObject(obj[key], level + 1, key)}
            {index < keys.length - 1 && <span className="text-slate-600">,</span>}
          </div>
        ))}
        <span className="text-slate-600">{'}'}</span>
      </div>
    );
  };

  const renderDataItem = (item, index) => {
    const itemId = item.id || index;
    const isExpanded = expandedItems.has(itemId);
    
    // Get primary identifier for the item
    const primaryId = item.id || item.name || index;
    const secondaryInfo = item.source && item.target ? `${item.source} → ${item.target}` :
                         item.entity ? item.entity :
                         item.name || item.provider || 'N/A';

    return (
      <Card key={itemId} className="card mb-4">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => toggleExpanded(itemId)}
                className="p-1 h-auto"
              >
                {isExpanded ? 
                  <ChevronDown className="w-4 h-4" /> : 
                  <ChevronRight className="w-4 h-4" />
                }
              </Button>
              <div>
                <CardTitle className="text-lg flex items-center space-x-2">
                  <Code className="w-4 h-4" />
                  <span>ID: {primaryId}</span>
                </CardTitle>
                <p className="text-sm text-slate-600 mt-1">{secondaryInfo}</p>
              </div>
            </div>
            <div className="flex space-x-2">
              <Button 
                size="sm" 
                variant="outline"
                onClick={() => copyToClipboard(item)}
                title="Copy to clipboard"
              >
                <Copy className="w-4 h-4" />
              </Button>
              <Button 
                size="sm" 
                variant="outline"
                onClick={() => downloadJson(item, `${activeTab}_${primaryId}`)}
                title="Download JSON"
              >
                <Download className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </CardHeader>

        {isExpanded && (
          <CardContent>
            <div className="bg-slate-50 border rounded-lg p-4 overflow-auto max-h-96">
              {selectedFormat === "formatted" ? (
                <div className="text-sm">
                  {renderJsonObject(item)}
                </div>
              ) : (
                <pre className="text-sm font-mono text-slate-700 whitespace-pre-wrap">
                  {formatJson(item)}
                </pre>
              )}
            </div>
          </CardContent>
        )}
      </Card>
    );
  };

  const getFilterOptions = () => {
    switch (activeTab) {
      case "entities":
        return [
          { value: "all", label: "All Entities" },
          { value: "with-dependencies", label: "With Dependencies" },
          { value: "with-incompatibles", label: "With Incompatibles" },
          { value: "with-delimiter", label: "With Delimiter" }
        ];
      case "sources":
        return [
          { value: "all", label: "All Sources" },
          { value: "with-pagination", label: "With Pagination" },
          { value: "with-auth", label: "With Authentication" }
        ];
      case "targets":
        return [
          { value: "all", label: "All Targets" },
          { value: "main-targets", label: "Main Targets" },
          { value: "mapping-targets", label: "Mapping Targets" },
          { value: "api-targets", label: "API Targets" }
        ];
      default:
        return [{ value: "all", label: "All Items" }];
    }
  };

  const getTabIcon = (tab) => {
    switch (tab) {
      case "entities": return <Database className="w-4 h-4" />;
      case "sources": return <FileText className="w-4 h-4" />;
      case "targets": return <Settings className="w-4 h-4" />;
      case "sourceFilters": return <Filter className="w-4 h-4" />;
      case "targetFields": return <List className="w-4 h-4" />;
      default: return <Braces className="w-4 h-4" />;
    }
  };

  if (loading) {
    return (
      <div className="space-y-4">
        <div className="h-8 bg-slate-200 rounded loading-skeleton"></div>
        <div className="h-64 bg-slate-200 rounded loading-skeleton"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold text-slate-800">Data Explorer</h2>
        <div className="flex space-x-2">
          <Button 
            variant="outline" 
            size="sm"
            onClick={() => downloadJson(filteredData, `${activeTab}_export`)}
          >
            <Download className="w-4 h-4 mr-2" />
            Export All
          </Button>
          <Button 
            variant="outline" 
            size="sm"
            onClick={() => copyToClipboard(filteredData)}
          >
            <Copy className="w-4 h-4 mr-2" />
            Copy All
          </Button>
        </div>
      </div>

      {/* Controls */}
      <Card className="card">
        <CardContent className="p-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">Search</label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  placeholder="Search in JSON data..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">Filter</label>
              <Select value={filterType} onValueChange={setFilterType}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {getFilterOptions().map(option => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">Format</label>
              <Select value={selectedFormat} onValueChange={setSelectedFormat}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="formatted">Formatted View</SelectItem>
                  <SelectItem value="compact">Compact JSON</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Data Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-5">
          <TabsTrigger value="entities" className="flex items-center space-x-2">
            {getTabIcon("entities")}
            <span>Entities</span>
          </TabsTrigger>
          <TabsTrigger value="sources" className="flex items-center space-x-2">
            {getTabIcon("sources")}
            <span>Sources</span>
          </TabsTrigger>
          <TabsTrigger value="targets" className="flex items-center space-x-2">
            {getTabIcon("targets")}
            <span>Targets</span>
          </TabsTrigger>
          <TabsTrigger value="sourceFilters" className="flex items-center space-x-2">
            {getTabIcon("sourceFilters")}
            <span>Source Filters</span>
          </TabsTrigger>
          <TabsTrigger value="targetFields" className="flex items-center space-x-2">
            {getTabIcon("targetFields")}
            <span>Target Fields</span>
          </TabsTrigger>
        </TabsList>

        {Object.keys(dataMap).map(key => (
          <TabsContent key={key} value={key} className="mt-6">
            <div className="mb-4 flex items-center justify-between">
              <div className="flex items-center space-x-4">
                <Badge className="badge-info">
                  {filteredData.length} items
                </Badge>
                {searchTerm && (
                  <Badge variant="outline">
                    Filtered by: "{searchTerm}"
                  </Badge>
                )}
                {filterType !== "all" && (
                  <Badge variant="outline">
                    Filter: {getFilterOptions().find(opt => opt.value === filterType)?.label}
                  </Badge>
                )}
              </div>
              
              {filteredData.length > 0 && (
                <div className="flex space-x-2">
                  <Button 
                    size="sm" 
                    variant="outline"
                    onClick={() => {
                      const allIds = filteredData.map((item, index) => item.id || index);
                      setExpandedItems(new Set(allIds));
                    }}
                  >
                    <Eye className="w-4 h-4 mr-2" />
                    Expand All
                  </Button>
                  <Button 
                    size="sm" 
                    variant="outline"
                    onClick={() => setExpandedItems(new Set())}
                  >
                    <EyeOff className="w-4 h-4 mr-2" />
                    Collapse All
                  </Button>
                </div>
              )}
            </div>

            {filteredData.length > 0 ? (
              <div className="space-y-4">
                {filteredData.map((item, index) => renderDataItem(item, index))}
              </div>
            ) : (
              <Card className="card">
                <CardContent className="p-8 text-center">
                  <Braces className="w-12 h-12 text-slate-400 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-slate-800 mb-2">No data found</h3>
                  <p className="text-slate-600">
                    {searchTerm || filterType !== "all" 
                      ? 'Try adjusting your search or filter criteria.' 
                      : 'No data available for this section.'
                    }
                  </p>
                </CardContent>
              </Card>
            )}
          </TabsContent>
        ))}
      </Tabs>
    </div>
  );
};

export default JsonViewer;